import React, { useEffect, useRef } from "react";
import BoardComment from "@/features/Board/BoardComment/BoardComment";
import { useInfiniteQuery } from "@tanstack/react-query";
import { axiosApi } from "@/config/axios.config";
import BoardCommentStatus from "./BoardCommentStatus/BoardCommentStatus";
import LoadingSpiner from "@/components/ui/loading-spiner";

export type Author =
  | {
      role: "admin" | "super";
      admin_email: string;
      nickname: string;
      guest_id?: undefined;
      profile_img: string | null;
    }
  | {
      role: "guest";
      guest_id: number;
      nickname: string;
      admin_email?: undefined;
      profile_img: string | null;
    };

export type CommentItemModel = {
  id: number;
  comment: string;
  parent_id: number | null;
  created_at: string;
  author: Author;
  post_id?: number;
  children: CommentItemModel[];
  author_type: "guest" | "admin";
};

export type InfinityItems<T extends undefined | number> = {
  result: { list: CommentItemModel[]; isNextPage: boolean };
} & (T extends undefined
  ? { status: { total: number; today: number } }
  : object);

const BoardCommentList = (): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);
  const { data, isFetching, isSuccess, isPending, hasNextPage, fetchNextPage } =
    useInfiniteQuery({
      queryKey: ["GUESTBOARD"],
      queryFn: async ({ pageParam }) => {
        let endPoint = "/guestboard";

        const curCursor = pageParam === 0 ? undefined : pageParam;
        if (curCursor) {
          endPoint += `?cursor=${curCursor}`;
        }

        const response =
          await axiosApi.get<InfinityItems<typeof curCursor>>(endPoint);
        return response.data.result;
      },
      getNextPageParam: (lastPage) => {
        if (lastPage.isNextPage) {
          return lastPage.list.at(-1)?.id;
        }
      },
      staleTime: 30 * 1000,
      gcTime: 60 * 1000,
      initialPageParam: 0,
    });

  // List
  const resultList = data?.pages.flatMap((e) => e.list) ?? [];

  useEffect(() => {
    if (!ref.current || !isSuccess) return;
    const currentRef = ref.current;
    const cb = (entries: IntersectionObserverEntry[]) => {
      if (entries[0].isIntersecting) {
        fetchNextPage();
      }
    };
    const io = new IntersectionObserver(cb, { threshold: 0.1 });
    io.observe(ref.current);

    return () => {
      io.unobserve(currentRef);
      io.disconnect();
    };
  }, [ref, isSuccess, fetchNextPage]);

  const isFirstPageResult = (page: {
    list: CommentItemModel[];
    isNextPage: boolean;
  }): page is {
    list: CommentItemModel[];
    isNextPage: boolean;
    status: { total: number; today: number };
  } => {
    return "status" in page;
  };

  const firstPage = data?.pages[0];

  const status =
    firstPage && isFirstPageResult(firstPage) ? firstPage.status : null;

  return (
    <>
      {/* 오늘 댓글 + 전체댓글  */}
      {isPending ? (
        <section className="flex flex-col gap-5">
          {[1, 2, 3, 4].map((_, idx) => (
            <React.Fragment key={`skeleton:${idx}`}>
              <div data-testid="loading" className="mt-5 flex flex-col gap-3">
                <div className="flex gap-5 items-center">
                  <div className="size-10 bg-foreground/10 rounded-full animate-pulse" />
                  <div className="h-4 bg-foreground/10 w-[100px] rounded-full animate-pulse" />
                  <div className="h-4 bg-foreground/10 w-[50px] rounded-full animate-pulse" />
                </div>
                <div className="h-4 bg-foreground/10 w-2/3 rounded-full animate-pulse" />
                <div className="h-4 bg-foreground/10 w-full rounded-full animate-pulse" />
              </div>
            </React.Fragment>
          ))}
        </section>
      ) : (
        data && (
          <BoardCommentStatus
            todayReply={status?.today ?? 0}
            total={status?.total ?? 0}
          />
        )
      )}

      <section
        className="grid grid-cols-1  gap-2 py-5 "
        data-testid="list-wrapper"
      >
        {/* 뿌리기 */}
        {resultList.map((item, idx) => {
          return (
            <BoardComment
              key={`guestbook-${idx}`}
              item={item}
              deps={1}
              rootId={item.id}
            />
          );
        })}
        {isFetching && (
          <div className="relative w-full h-[50px]">
            <LoadingSpiner />
          </div>
        )}
        {hasNextPage && <div className="h-[50px] w-full" ref={ref}></div>}
      </section>
    </>
  );
};

export default BoardCommentList;
